import React, {
  createContext, FC, useContext, useReducer,
} from 'react';
import { ActionFromPayloadMap, PlannedTwinID, PlanResponseLog } from '../config/types';
import CsvReducer from '../reducers/plannedTwinIDUpdate';
import { PlannedTwinIDUpdateMode } from '../config/constants';

export type PlannedTwinIDUpdateStateType = {
  isPlanningPlannedTwinIDs: boolean
  isApplyingPlannedTwinIDs: boolean
  plannedUpdatedPlannedTwinID: {
    updateByTwinID: number,
    unchanged: number,
    create: number,
    delete: number,
    totalCount: number,
    updates: {
      byTwinID: PlannedTwinID[],
      created: PlannedTwinID[],
      deleted: PlannedTwinID[]
    },
    logging: {
      errors: PlanResponseLog[],
      warnings: PlanResponseLog[],
      info: PlanResponseLog[],
      errorCount: 0,
      warningCount: 0,
      infoCount: 0
    }
  } | null
  existingPlannedTwinIDs: PlannedTwinID[] | null,
  fileName: string | null
  importTime: Date | null
  fileContents: string | null
  plannedTwinIDUpdateMode: PlannedTwinIDUpdateMode
  showChangesOnly: boolean
  error: Error | null
};

const initialState: PlannedTwinIDUpdateStateType = {
  isPlanningPlannedTwinIDs: false,
  isApplyingPlannedTwinIDs: false,
  plannedUpdatedPlannedTwinID: null,
  existingPlannedTwinIDs: null,
  fileName: null,
  importTime: null,
  fileContents: null,
  plannedTwinIDUpdateMode: 'upsert',
  showChangesOnly: false,
  error: null,
};

export type PlannedTwinIDUpdateActionPayloadMap = {
  SET_PLANNED_TWIN_ID_UPDATE_FILE: { name: string; contents: string };
  SET_IS_PLANNING_PLANNED_TWIN_ID_UPDATE: boolean;
  SET_PLANNED_TWIN_ID_UPDATE_PLAN: PlannedTwinIDUpdateStateType['plannedUpdatedPlannedTwinID'];
  SET_EXISTING_PLANNED_TWIN_IDS: PlannedTwinIDUpdateStateType['existingPlannedTwinIDs'];
  SET_IS_APPLYING_PLANNED_TWIN_ID_UPDATE: boolean;
  SET_PLANNED_TWIN_ID_UPDATE_ERROR: PlannedTwinIDUpdateStateType['error'];
  RESET_PLANNED_TWIN_ID_UPDATE: undefined;
  SET_PLANNED_TWIN_ID_UPDATE_MODE: PlannedTwinIDUpdateMode;
  SET_PLANNED_TWIN_ID_UPDATE_SHOW_CHANGES_ONLY: boolean;
};

export type PlannedTwinIDUpdateAction = ActionFromPayloadMap<PlannedTwinIDUpdateActionPayloadMap>;

export const PlannedTwinIDUpdateContext = createContext({
  state: initialState,
  dispatch: (() => { }) as React.Dispatch<PlannedTwinIDUpdateAction>,
});

export const usePlannedTwinIDUpdate = (): [
  PlannedTwinIDUpdateStateType, React.Dispatch<PlannedTwinIDUpdateAction>,
] => {
  const { state, dispatch } = useContext(PlannedTwinIDUpdateContext);
  return [state, dispatch];
};

export const PlannedTwinIDUpdateProvider:FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [state, dispatch] = useReducer(CsvReducer, initialState);

  return (
    <PlannedTwinIDUpdateContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </PlannedTwinIDUpdateContext.Provider>
  );
};
